// Update: components/ColorPicker.tsx
import { css } from 'aphrodite';
import React, { useEffect, useRef, useState } from 'react';
import { HexColorInput, HexColorPicker } from 'react-colorful';
import { styleSheet } from './styles';

const DARK_COLOR_THRESHOLD = '#a0a0a0';
interface ColorPickerProps {
	value: string;
	onChange: (color: string) => void;
}

// Convert hex to RGB
const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
	const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
	return result
		? {
				r: parseInt(result[1], 16),
				g: parseInt(result[2], 16),
				b: parseInt(result[3], 16),
			}
		: null;
};

// Calculate relative luminance using sRGB coefficients
const getLuminance = (r: number, g: number, b: number): number => {
	const [rs, gs, bs] = [r, g, b].map(c => {
		c = c / 255;
		return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
	});
	return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
};

// Check if color is darker than threshold
const isDarkerThan = (color: string, threshold: string): boolean => {
	const rgb1 = hexToRgb(color);
	const rgb2 = hexToRgb(threshold);
	if (!rgb1 || !rgb2) return false;

	const lum1 = getLuminance(rgb1.r, rgb1.g, rgb1.b);
	const lum2 = getLuminance(rgb2.r, rgb2.g, rgb2.b);

	return lum1 <= lum2;
};

const ColorPicker: React.FC<ColorPickerProps> = ({ value, onChange }) => {
	const [isOpen, setIsOpen] = useState(false);
	const popoverRef = useRef<HTMLDivElement>(null);

	// Close picker when clicking outside
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
				setIsOpen(false);
			}
		};

		if (isOpen) {
			document.addEventListener('mousedown', handleClickOutside);
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [isOpen]);

	const normalizedValue = value?.toLowerCase() || '#000000';
	const isDark = isDarkerThan(normalizedValue, DARK_COLOR_THRESHOLD);

	return (
		<div className={css(styleSheet.container)}>
			<div
				className={css(styleSheet.colorBox)}
				style={{ backgroundColor: normalizedValue }}
				onClick={() => setIsOpen(open => !open)}
				aria-label='Open color picker'
				tabIndex={0}
				role='button'
			>
				<span className={css(styleSheet.hexOverlay, isDark && styleSheet.hexOverlayWhite)}>
					{normalizedValue.toUpperCase()}
				</span>
			</div>
			{isOpen && (
				<div ref={popoverRef} className={css(styleSheet.popover)}>
					<div className={css(styleSheet.popoverContainer)}>
						<HexColorPicker color={value} onChange={onChange} />
						<div>
							<label className={css(styleSheet.inputLabel)} htmlFor='hex-input'>
								Hex
							</label>
							<HexColorInput
								id='hex-input'
								color={value.toUpperCase()}
								onChange={onChange}
								className={css(styleSheet.hexInput)}
								prefixed
								aria-label='Hex color input'
							/>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export { ColorPicker };
